import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { adminApi, AdminUser } from '../../shared/api/admin.api';
import { useAccountStore } from '../../entities/account';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { toast } from 'sonner@2.0.3';

const userLabel = (u: AdminUser) => u.full_name ?? u.username ?? u.id;
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Доступы сотрудника к Instagram-аккаунтам.
 *
 * Доступы TikTok/YouTube/Threads сюда не входят: назначения на бэкенде —
 * только список Instagram-аккаунтов (02.11.2026).
 */
export function AssignmentsPage() {
  const accounts = useAccountStore((s) => s.accounts);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  /** Сохранённое на сервере — для «есть несохранённое». */
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [assignedIds, setAssignedIds] = useState<string[]>([]);
  /** Чьи доступы сейчас на экране: пока грузятся новые — ничей, сохранять нельзя. */
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [copying, setCopying] = useState(false);
  const requestRef = useRef(0);

  const loadUsers = () => {
    setUsersError(null);
    adminApi.listUsers()
      .then(({ users }) => {
        const employees = users.filter((u) => u.role === 'employee');
        setUsers(employees);
        if (employees.length > 0) setSelectedUserId((prev) => prev || employees[0].id);
      })
      .catch((e) => setUsersError(e instanceof Error ? e.message : 'Не удалось загрузить сотрудников'));
  };
  useEffect(loadUsers, []);

  const loadAssignments = (userId: string) => {
    // Ответ по прошлому сотруднику, пришедший позже, не должен лечь на нового:
    // раньше его галочки можно было сохранить следующему.
    const request = ++requestRef.current;
    setLoadedFor(null);
    setLoadError(null);
    adminApi.getAssignments(userId)
      .then(({ accountIds }) => {
        if (request !== requestRef.current) return;
        setSavedIds(accountIds);
        setAssignedIds(accountIds);
        setLoadedFor(userId);
      })
      .catch((e) => {
        if (request !== requestRef.current) return;
        setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить доступы');
      });
  };
  useEffect(() => {
    if (selectedUserId) loadAssignments(selectedUserId);
  }, [selectedUserId]);

  const isLoaded = loadedFor === selectedUserId && Boolean(selectedUserId);
  const dirty = isLoaded && !sameSet(savedIds, assignedIds);

  const visibleAccounts = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^@/, '');
    if (!q) return accounts;
    return accounts.filter((a) => `${a.username ?? ''} ${a.name ?? ''} ${(a.tags ?? []).join(' ')}`.toLowerCase().includes(q));
  }, [accounts, query]);

  const allTags = useMemo(
    () => [...new Set(accounts.flatMap((a) => a.tags ?? []))].sort((a, b) => a.localeCompare(b, 'ru')),
    [accounts],
  );

  async function switchUser(userId: string) {
    if (userId === selectedUserId) return;
    if (dirty) {
      const ok = await confirmDialog({
        title: 'Доступы не сохранены',
        description: `Изменения для «${userLabel(users!.find((u) => u.id === selectedUserId)!)}» пропадут.`,
        confirmText: 'Переключиться',
        destructive: true,
      });
      if (!ok) return;
    }
    setSelectedUserId(userId);
  }

  function toggleAccount(accountId: string) {
    setAssignedIds((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  }

  const addIds = (ids: string[]) => setAssignedIds((prev) => [...new Set([...prev, ...ids])]);
  const removeIds = (ids: string[]) => setAssignedIds((prev) => prev.filter((id) => !ids.includes(id)));

  function selectByTag(tag: string) {
    const ids = accounts.filter((a) => (a.tags ?? []).includes(tag)).map((a) => a.id);
    addIds(ids);
    toast.success(`Тэг «${tag}»: добавлено ${ids.length}`);
  }

  async function copyFrom(userId: string) {
    const source = users?.find((u) => u.id === userId);
    setCopying(true);
    try {
      const { accountIds } = await adminApi.getAssignments(userId);
      // Только аккаунты, которые есть на экране, — иначе «Выбрано» не сходится.
      const known = accountIds.filter((id) => accounts.some((a) => a.id === id));
      setAssignedIds(known);
      toast.success(`Доступы как у «${source ? userLabel(source) : userId}»: ${known.length}. Проверьте и сохраните.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось взять доступы');
    } finally {
      setCopying(false);
    }
  }

  async function handleSave() {
    if (!isLoaded || !loadedFor) return;
    setSaving(true);
    try {
      await adminApi.setAssignments(loadedFor, assignedIds);
      setSavedIds(assignedIds);
      toast.success('Доступы сохранены');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (usersError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Сотрудники не загрузились: {usersError}</p>
        <Button size="sm" variant="outline" onClick={loadUsers}>Повторить</Button>
      </div>
    );
  }
  if (!users) return <p className="text-muted-foreground">Загрузка...</p>;

  const visibleIds = visibleAccounts.map((a) => a.id);
  const others = users.filter((u) => u.id !== selectedUserId);

  return (
    <div className="space-y-4">
      {users.length === 0 ? (
        <p className="text-muted-foreground">Нет сотрудников. Пригласите их на вкладке «Сотрудники».</p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 w-full max-w-xs">
              <Label>Сотрудник</Label>
              <Select value={selectedUserId} onValueChange={(v) => void switchUser(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{userLabel(u)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {others.length > 0 && (
              <Select value="" onValueChange={(v) => v && void copyFrom(v)} disabled={!isLoaded || copying}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={copying ? 'Берём доступы…' : 'Скопировать доступы от сотрудника…'} />
                </SelectTrigger>
                <SelectContent>
                  {others.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{userLabel(u)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="border rounded-md p-4 space-y-3 max-w-md">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Instagram аккаунты</p>
              {isLoaded && (
                <span className="text-xs text-muted-foreground tabular-nums">Выбрано {accounts.filter((a) => assignedIds.includes(a.id)).length} из {accounts.length}</span>
              )}
            </div>

            {loadError ? (
              <div className="space-y-2">
                <p className="text-sm text-destructive">Доступы не загрузились: {loadError}</p>
                <Button size="sm" variant="outline" onClick={() => loadAssignments(selectedUserId)}>Повторить</Button>
              </div>
            ) : !isLoaded ? (
              // Пока грузятся доступы нового сотрудника, старые галочки не показываем.
              <div className="space-y-2">
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-5 w-48" />)}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск по нику или тэгу" className="pl-9" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => addIds(visibleIds)} disabled={visibleIds.length === 0}>
                    Выбрать все{query.trim() ? ' найденные' : ''}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeIds(visibleIds)} disabled={visibleIds.length === 0}>
                    Снять все{query.trim() ? ' найденные' : ''}
                  </Button>
                  {allTags.length > 0 && (
                    <Select value="" onValueChange={(v) => v && selectByTag(v)}>
                      <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Выбрать по тэгу" /></SelectTrigger>
                      <SelectContent>
                        {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {visibleAccounts.length === 0 && (
                  <p className="text-sm text-muted-foreground">Ничего не найдено</p>
                )}
                {visibleAccounts.map((account) => (
                  <div key={account.id} className="flex items-center gap-3">
                    <Checkbox
                      id={`assign-${account.id}`}
                      checked={assignedIds.includes(account.id)}
                      onCheckedChange={() => toggleAccount(account.id)}
                    />
                    <Label htmlFor={`assign-${account.id}`} className="font-normal cursor-pointer">
                      @{account.username ?? account.name}
                    </Label>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving || !isLoaded || !dirty}>
              {saving && <Loader2 className="animate-spin" />}
              {saving ? 'Сохранение...' : 'Сохранить доступы'}
            </Button>
            {dirty && <span className="text-xs text-muted-foreground">Есть несохранённые изменения</span>}
          </div>
        </>
      )}
    </div>
  );
}
